import React, { useState } from 'react';
import { CheckCircle, DollarSign, Shield, TrendingUp, ChevronDown } from 'lucide-react';
import CTASection from '../../components/CTASection';
import ServiceHeroForm from '../../components/ServiceHeroForm';
import RegistrationModal from '../../components/RegistrationModal';

const SCorpFormationPage = () => {
  const [openFAQ, setOpenFAQ] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('registration');

  const openModal = (type) => {
    setModalType(type);
    setIsModalOpen(true);
  };

  const benefits = [
    { icon: DollarSign, title: 'Tax Savings', description: 'Save 15.3% self-employment tax on distributions' },
    { icon: Shield, title: 'Liability Protection', description: 'Personal assets protected from business debts' },
    { icon: CheckCircle, title: 'Pass-Through Taxation', description: 'Profits taxed only once at personal rate' },
    { icon: TrendingUp, title: 'Business Credibility', description: 'Professional structure builds trust' }
  ];

  const packages = [
    {
      name: 'S-Corp Formation',
      price: '$499',
      description: 'Complete S-Corp setup with IRS election',
      features: ['Entity formation (LLC or Corp)', 'IRS Form 2553 (S-Corp election)', 'Federal EIN', 'Operating Agreement/Bylaws', 'Registered agent (1 year)', 'State filing fees', '7-10 business days'],
      recommended: true
    }
  ];

  const faqs = [
    {
      question: 'What is an S-Corporation and how does it save taxes?',
      answer: 'An S-Corporation is a tax election, not a separate entity type. You form an LLC or C-Corp, then elect S-Corp taxation with the IRS. The key tax benefit: S-Corp owners pay themselves a "reasonable salary" subject to 15.3% self-employment tax (Social Security + Medicare), but additional profits taken as "distributions" are not subject to self-employment tax, only income tax. Example: $150K profit. As sole proprietor, you pay $22,950 in self-employment tax. As S-Corp with $80K salary + $70K distribution, you pay $12,240 in self-employment tax. Savings: $10,710 annually.'
    },
    {
      question: 'How is an S-Corp different from an LLC or C-Corp?',
      answer: 'S-Corp is a tax election, not an entity structure. LLC: Default pass-through taxation, all profits subject to self-employment tax (15.3%). S-Corp: Pass-through taxation, only salary (not distributions) subject to self-employment tax. Saves money but requires payroll. C-Corp: Corporate income tax (21%) plus personal tax on dividends (double taxation). Best for companies reinvesting profits or seeking VC funding. Most profitable small businesses (over $60K profit) benefit from S-Corp election to reduce self-employment taxes.'
    },
    {
      question: 'What is a "reasonable salary" for S-Corp owners?',
      answer: 'The IRS requires S-Corp owners who work in the business to pay themselves a "reasonable salary" subject to payroll taxes before taking distributions. Reasonable means: comparable to what you\'d pay someone else to do your job, based on industry standards for your role and location, considering company profitability and time commitment. General guidelines: Service businesses: 40-60% of profits as salary. Product businesses: 30-50% of profits as salary. If you pay yourself too little in salary and too much in distributions, the IRS can reclassify distributions as wages and assess penalties plus back taxes. We provide industry-specific salary guidance.'
    },
    {
      question: 'What are the requirements and costs to maintain an S-Corp?',
      answer: 'S-Corps have more requirements than standard LLCs: Payroll processing: Must run payroll for owner-employees ($30-100/month or DIY). Form 1120-S: Annual corporate tax return ($300-800 if using accountant). State taxes: Annual reports and franchise taxes (varies by state, $0-800). Reasonable salary: IRS requires W-2 wages for working owners. Quarterly estimated taxes: Pay taxes on projected annual income. Total additional annual cost: $1,500-$3,000 in accounting and payroll. However, if your business profits exceed $60K, the self-employment tax savings ($10K+ annually) far outweigh the extra costs.'
    },
    {
      question: 'When should I elect S-Corp status?',
      answer: 'Elect S-Corp when: Business profits consistently exceed $60,000/year (the tax savings justify the payroll costs). You actively work in the business (can justify reasonable salary). You have consistent cash flow to pay yourself regular salary. You\'re willing to run payroll and maintain corporate formalities. NOT recommended if: Profits under $40-60K (costs exceed savings). You want to retain all profits in the business. You have losses (pass-through losses may be more valuable without salary requirement). You plan to seek VC funding (VCs prefer C-Corps). Deadline: File Form 2553 within 75 days of formation or by March 15 for current year election.'
    },
    {
      question: 'Can I convert my existing LLC to an S-Corp?',
      answer: 'Yes! If you already have an LLC, you can elect S-Corp taxation by filing IRS Form 2553. Your LLC remains an LLC for legal purposes but is taxed as an S-Corp. This is called "LLC taxed as S-Corp" and is the most common approach. Benefits: Keep LLC flexibility and simplicity for legal matters, gain S-Corp tax savings on self-employment taxes, and avoid forming a new entity. Requirements: Must meet S-Corp eligibility (100 or fewer shareholders, all US citizens/residents, one class of ownership). File Form 2553 by March 15 for current year election or within 75 days of formation. We handle the entire election process including Form 2553 preparation and IRS filing.'
    }
  ];

  return (
    <div>
      <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="max-w-3xl">
              <div className="inline-block bg-[#168356]/20 text-[#168356] px-4 py-2 rounded-full text-sm font-semibold mb-6">S-Corporation Formation</div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Save Thousands in Taxes with <span className="text-[#168356]">S-Corp Election</span></h1>
              <p className="text-xl text-gray-300 mb-8">Reduce self-employment taxes by 15.3% on business profits while maintaining pass-through taxation and liability protection.</p>
              <div className="flex gap-4">
                <button onClick={() => openModal('registration')} className="bg-[#d29b00] hover:bg-[#a87b00] text-white font-bold px-8 py-4 rounded-lg transition-all">Calculate Your Tax Savings</button>
              </div>
              <div className="flex items-center gap-6 mt-8 text-sm">
                <span className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-[#168356]" />Save $10K+ Annually</span>
                <span className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-[#168356]" />Pass-Through Taxation</span>
              </div>
            </div>
          </div>
          <ServiceHeroForm title="Start Your S-Corp Election" buttonText="Start S-Corp Election" />
        </div>
      </div>

      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12"><h2 className="text-4xl font-bold text-black mb-4">How S-Corp Tax Savings Work</h2></div>
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-[#168356]/10 to-green-50 rounded-xl p-8 border border-[#168356]">
            <h3 className="text-2xl font-bold text-black mb-6">Example: $150,000 Business Profit</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-500">
                <h4 className="font-bold text-red-600 mb-3">As LLC/Sole Proprietor</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span>Business Profit:</span><span className="font-bold">$150,000</span></div>
                  <div className="flex justify-between"><span>Self-Employment Tax (15.3%):</span><span className="font-bold text-red-600">-$22,950</span></div>
                  <div className="flex justify-between"><span>Income Tax (24% bracket):</span><span className="font-bold">-$30,492</span></div>
                  <div className="border-t-2 border-red-300 pt-2 flex justify-between"><span className="font-bold">Net After Taxes:</span><span className="font-bold">$96,558</span></div>
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-6 border-l-4 border-green-500">
                <h4 className="font-bold text-green-600 mb-3">As S-Corporation</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span>Business Profit:</span><span className="font-bold">$150,000</span></div>
                  <div className="flex justify-between"><span>Salary: $80,000 (SE tax):</span><span className="font-bold text-red-600">-$12,240</span></div>
                  <div className="flex justify-between"><span>Distribution: $70,000 (no SE tax):</span><span className="font-bold">$0</span></div>
                  <div className="flex justify-between"><span>Income Tax (24% bracket):</span><span className="font-bold">-$30,492</span></div>
                  <div className="border-t-2 border-green-300 pt-2 flex justify-between"><span className="font-bold">Net After Taxes:</span><span className="font-bold">$107,268</span></div>
                </div>
              </div>
            </div>
            <div className="mt-6 text-center p-4 bg-white rounded-lg">
              <div className="text-3xl font-bold text-[#168356]">Annual Tax Savings: $10,710</div>
              <div className="text-sm text-gray-600 mt-2">Over 10 years: $107,100+ in tax savings</div>
            </div>
          </div>
        </div>
      </div>

      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12"><h2 className="text-4xl font-bold text-black mb-4">S-Corp Formation Package</h2></div>
          <div className="max-w-2xl mx-auto">
            {packages.map((pkg, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg border-2 border-[#168356]">
                <div className="bg-[#168356] text-white text-sm font-bold px-4 py-1 rounded-full inline-block mb-4">Complete S-Corp Setup</div>
                <h3 className="text-2xl font-bold text-black mb-2">{pkg.name}</h3>
                <div className="text-4xl font-bold text-[#168356] mb-4">{pkg.price}<span className="text-lg text-gray-500"> + state fees</span></div>
                <p className="text-gray-600 mb-6">{pkg.description}</p>
                <ul className="space-y-3 mb-6">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700"><CheckCircle className="w-4 h-4 text-[#168356] flex-shrink-0 mt-0.5" /><span>{feature}</span></li>
                  ))}
                </ul>
                <button onClick={() => openModal('registration')} className="w-full py-3 rounded-lg font-semibold transition-all bg-[#d29b00] text-white hover:bg-[#a87b00]">Get Started</button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12"><h2 className="text-4xl font-bold text-black mb-4">Benefits of S-Corporation</h2></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-[#168356] rounded-full flex items-center justify-center mx-auto mb-4"><benefit.icon className="w-7 h-7 text-white" /></div>
                <h3 className="text-xl font-bold text-black mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12"><h2 className="text-4xl font-bold text-black mb-4">Frequently Asked Questions</h2></div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-sm">
                <button onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)} className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                  <span className="text-left font-semibold text-black pr-8">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-[#168356] flex-shrink-0 transition-transform ${openFAQ === index ? 'rotate-180' : ''}`} />
                </button>
                {openFAQ === index && <div className="px-6 pb-4 text-gray-700 leading-relaxed">{faq.answer}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <CTASection />

      <RegistrationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialType={modalType}
      />
    </div>
  );
};

export default SCorpFormationPage;